import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLotteryContract } from '../contracts/hooks';
import {
  Container,
  Card,
} from './styled/index';
import { Toast } from './Toast';
import { LotteryDetails, TicketPurchaseParams } from '../contracts/interfaces';
import LotteryInfo from './LotteryInfo';
import styled, { keyframes } from 'styled-components';
import { ChevronDown } from 'lucide-react';

import { LotteryNumberSelector } from './LotteryNumberSelector';
import { LotteryActionPanel } from './LotteryActionPanel';
import { LotteryWelcomeInfo } from './LotteryWelcomeInfo';
import { NetworkSwitchBanner } from './NetworkSwitchBanner';
import { HeroJackpot } from './HeroJackpot';
import { TrustBadges } from './TrustBadges';

const LotteryDashboardWrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  background: ${({ theme }) => theme.colors.background};
  background-image: ${({ theme }) => theme.gradients.mesh};
`;

const DashboardBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
  background: radial-gradient(ellipse at 50% 0%, rgba(99, 102, 241, 0.15) 0%, transparent 50%),
              radial-gradient(ellipse at 100% 50%, rgba(168, 85, 247, 0.1) 0%, transparent 40%),
              radial-gradient(ellipse at 0% 100%, rgba(0, 208, 192, 0.1) 0%, transparent 40%);
`;

interface DashboardContentProps {
  $hasNetworkBanner?: boolean;
}

const DashboardContent = styled.div<DashboardContentProps>`
  position: relative;
  z-index: 10;
  min-height: 100vh;
  padding: 2rem;
  padding-top: ${props => props.$hasNetworkBanner ? '6rem' : '2rem'};
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem;
    padding-top: ${props => props.$hasNetworkBanner ? '5rem' : '1rem'};
  }
`;

const MainCard = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: 24px;
  padding: 2rem;
  position: relative;
  box-shadow: ${({ theme }) => theme.shadows.card};

  /* Gradient border */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: 1px;
    border-radius: 24px;
    background: ${({ theme }) => theme.gradients.cardBorder};
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(to right, ${({ theme }) => theme.colors.glassBorder}, transparent);
  }
`;

const LoadingOverlay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 100;
`;

const LoadingIndicator = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 20px;
  text-align: center;
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SpinnerDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  margin: 0 5px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  animation: dot-flashing 1s infinite alternate;

  @keyframes dot-flashing {
    0% {
      opacity: 0.4;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.4;
    }
  }

  &:nth-child(2) {
    animation-delay: 0.2s;
  }

  &:nth-child(3) {
    animation-delay: 0.4s;
  }
`;

const LoadingText = styled.p`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.1rem;
  margin-top: 15px;
  font-weight: bold;
  text-align: center;
`;

const SpinningIcon = styled.span`
  display: inline-block;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

/* ============ Scroll Down Indicator ============ */
const bounceDown = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(8px); }
`;

const ScrollIndicator = styled.div<{ $visible: boolean }>`
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity 0.3s ease;
  cursor: pointer;

  @media (max-width: 480px) {
    bottom: 1.5rem;
  }
`;

const ScrollArrow = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(219, 255, 0, 0.15);
  border: 1px solid rgba(219, 255, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${bounceDown} 1.5s ease-in-out infinite;
  backdrop-filter: blur(8px);

  svg {
    color: rgba(219, 255, 0, 0.9);
  }

  &:hover {
    background: rgba(219, 255, 0, 0.25);
    border-color: rgba(219, 255, 0, 0.6);
  }
`;

const ScrollText = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: rgba(219, 255, 0, 0.7);
`;

// Extracted LoadingSpinner component
const LoadingSpinner: React.FC = () => (
  <LoadingOverlay>
    <LoadingIndicator>
      <SpinnerContainer>
        <SpinnerDot />
        <SpinnerDot />
        <SpinnerDot />
      </SpinnerContainer>
      <LoadingText>Processing, please wait...</LoadingText>
    </LoadingIndicator>
  </LoadingOverlay>
);


const LotteryDashboard: React.FC = () => {
  // Contract-related state from hook
  const {
    getLotteryDetails,
    getTotalTickets,
    purchaseTicket,
    isConnected,
    isCorrectNetwork,
    connectWallet,
    switchNetwork,
    signer,
    winningsManagerContract,
  } = useLotteryContract();

  // Add debug logging for network state
  useEffect(() => {
    console.debug('[Network State]', { isConnected, isCorrectNetwork });
  }, [isConnected, isCorrectNetwork]);

  // Track scroll position for scroll indicator
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      // Hide indicator after scrolling 150px
      setShowScrollIndicator(scrollY < 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollDown = useCallback(() => {
    window.scrollBy({ top: window.innerHeight * 0.6, behavior: 'smooth' });
  }, []);

  // Component state
  const [lotteryData, setLotteryData] = useState<LotteryDetails | null>(null);
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [pendingWithdrawal, setPendingWithdrawal] = useState<bigint>(BigInt(0));
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [ticketsSold, setTicketsSold] = useState<number>(0);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);

  // Debug logger that only runs in development and tracks previous state
  const prevStateRef = useRef<any>(null);
  const debugLog = useCallback((message: string, data?: any) => {
    if (process.env.NODE_ENV === 'development') {
      // Only log if data has changed
      if (data && prevStateRef.current) {
        const hasChanged = JSON.stringify(data) !== JSON.stringify(prevStateRef.current);
        if (!hasChanged) return;
      }
      if (data) prevStateRef.current = data;
      console.log(`[Lottery] ${message}`, data || '');
    }
  }, []);

  // Single effect to track important state changes
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const currentState = {
        wallet: { connected: isConnected, correctNetwork: isCorrectNetwork, hasSigner: !!signer },
        lottery: { hasData: !!lotteryData, isLoading, isFirstLoad },
      };
      debugLog('State', currentState);
    }
  }, [isConnected, isCorrectNetwork, lotteryData, isLoading, isFirstLoad, signer, debugLog]);

  // Memoize the fetch lottery details function to prevent unnecessary re-renders in useEffect
  const memoizedGetLotteryDetails = useCallback(async () => {
    try {
      debugLog('Fetching lottery details', { isFirstLoad, isLoading });
      const details = await getLotteryDetails();
      
      if (details) {
        setLotteryData(details);
        if (isFirstLoad) {
          setIsFirstLoad(false);
          setIsLoading(false);
          debugLog('Initial lottery data loaded');
        }
      }
    } catch (err) {
      setError('Failed to fetch lottery details');
      debugLog('Error fetching lottery details', err);
      
      if (isFirstLoad) {
        setIsFirstLoad(false);
        setIsLoading(false);
      }
    }
  }, [getLotteryDetails, isFirstLoad, isLoading, debugLog]);

  const memoizedGetTicketsSold = useCallback(async (lotteryId?: number) => {
    try {
      const total = await getTotalTickets(lotteryId);
      if (typeof total === 'number') setTicketsSold(total);
      if (process.env.NODE_ENV === 'development') {
        console.debug('[LotteryDashboard] ticketsSold', { lotteryId, total });
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[LotteryDashboard] ticketsSold fetch error', err);
      }
    }
  }, [getTotalTickets]);

  // Effect to handle connection state and data fetching
  useEffect(() => {
    debugLog('Connection state changed', {
      isConnected,
      isCorrectNetwork,
      isFirstLoad,
      isLoading
    });

    if (isConnected && isCorrectNetwork) {
      if (isFirstLoad) {
        setIsLoading(true);
        debugLog('Initial connection established');
      }
      memoizedGetLotteryDetails();
    }
    
    // If lottery is in drawing state, set a timeout to refresh after expected draw time
    if (lotteryData && lotteryData.state === 2) {
      const drawTimeout = setTimeout(() => {
        memoizedGetLotteryDetails(); // Refresh to get the next state
  }, 5 * 1000); // 5 seconds

      return () => clearTimeout(drawTimeout);
    }
  }, [
    isConnected,
    isCorrectNetwork,
    lotteryData?.state,
    memoizedGetLotteryDetails,
    isFirstLoad // Keep this to handle initial connection
  ]);

  useEffect(() => {
    if (!isConnected || !isCorrectNetwork) return;
    if (!lotteryData?.lotteryId) return;
    memoizedGetTicketsSold(lotteryData.lotteryId);
  }, [isConnected, isCorrectNetwork, lotteryData?.lotteryId, memoizedGetTicketsSold]);

  // Fetch pending withdrawal - use useCallback and avoid recreating the function
  const fetchPendingWithdrawal = useCallback(async () => {
    try {
      if (!isConnected || !winningsManagerContract || !signer) return;
      const signerAddress = await signer.getAddress();
      const withdrawalAmount =
        await winningsManagerContract.getPendingWithdrawal(signerAddress);
      setPendingWithdrawal(withdrawalAmount);
      debugLog('Updated pending withdrawal', { amount: withdrawalAmount.toString() });
    } catch (error) {
      debugLog('Error fetching pending withdrawal', error);
    }
  }, [isConnected, signer, winningsManagerContract]);

  useEffect(() => {
    fetchPendingWithdrawal();
  }, [fetchPendingWithdrawal]);

  // Timer effect with improved state management
  useEffect(() => {
    if (!lotteryData) {
      setTimeRemaining(0);
      return;
    }

    const updateTimer = async () => {
      const currentTime = Math.floor(Date.now() / 1000);
      const initialRemaining = Math.max(0, lotteryData.endTime - currentTime);

      // If time has expired, force a refresh
      if (initialRemaining <= 0) {
        try {
          await memoizedGetLotteryDetails();
          // Ensure we get fresh lottery data immediately
          setTimeRemaining(0);
        } catch (error) {
          debugLog('Failed to refresh lottery details', error);
        }
        return;
      }

      setTimeRemaining(initialRemaining);
    };

    // Initial update
    updateTimer();

    // Set up interval to update every second
    const timer = setInterval(updateTimer, 1000);

    return () => clearInterval(timer);
  }, [lotteryData, memoizedGetLotteryDetails]);

  // Error and Success message cleanup
  useEffect(() => {
    if (error) {
      const errorTimer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(errorTimer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const successTimer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(successTimer);
    }
  }, [success]);

  const handleConnectWallet = useCallback(async () => {
    try {
      await connectWallet();
    } catch (err) {
      setError('Failed to connect wallet');
      console.error(err);
    }
  }, [connectWallet]);

  // Memoize handlePurchaseTicket to prevent unnecessary re-renders in child components
  const handlePurchaseTicket = useCallback(
    async (numbers: number[]) => {
      if (!lotteryData) {
        setError('Lottery data not available');
        return;
      }

      if (lotteryData.state !== 1) {
        setError('Lottery is not currently active');
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        setSuccess(null);

        const purchaseParams: TicketPurchaseParams = {
          numbers,
          value: lotteryData.price,
        };

        const successResult = await purchaseTicket(purchaseParams);
        if (!successResult) {
          throw new Error('Ticket purchase failed');
        }

        setSuccess('Ticket purchased successfully!');
        setSelectedNumbers([]); // Reset selected numbers
      } catch (err: any) {
        let errorMessage = 'Failed to purchase ticket';
        if (
          err.message?.includes('ethers-user-denied') ||
          err.message?.includes('User denied transaction signature')
        ) {
          errorMessage = 'Transaction was cancelled by user';
        } else if (
          err.message?.includes('transaction execution reverted') ||
          err.code === 'CALL_EXCEPTION'
        ) {
          errorMessage =
            'Transaction failed. Please check your balance or try again.';
        }
        setError(errorMessage);
        console.error('Ticket purchase error:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [lotteryData, purchaseTicket]
  );

  const formatEth = useCallback((value: bigint) => {
    return (Number(value) / 1e18).toFixed(5);
  }, []);

  // Memoize handleWithdrawWinnings
  const handleWithdrawWinnings = useCallback(async () => {
    try {
      if (!winningsManagerContract || !signer) {
        debugLog('Withdrawal failed - no contract or signer');
        setError('Contract not initialized or no signer');
        return;
      }

      setIsLoading(true);
      setError(null);
      setSuccess(null);

      const signerAddress = await signer.getAddress();
      const pendingAmount = await winningsManagerContract.getPendingWithdrawal(
        signerAddress
      );

      if (pendingAmount === BigInt(0)) {
        setError('No winnings available to withdraw');
        return;
      }

      debugLog('Initiating withdrawal', { amount: pendingAmount.toString() });
      const tx = await winningsManagerContract.withdrawWinnings();
      await tx.wait();

      const pendingAfterWithdrawal =
        await winningsManagerContract.getPendingWithdrawal(signerAddress);

      setPendingWithdrawal(pendingAfterWithdrawal);
      setSuccess(`Successfully withdrew ${formatEth(pendingAmount)} ETH`);
      debugLog('Withdrawal complete', { 
        withdrawn: pendingAmount.toString(),
        remaining: pendingAfterWithdrawal.toString() 
      });
    } catch (err: any) {
      let errorMessage = 'Failed to withdraw winnings';
      if (
        err.message?.includes('ethers-user-denied') ||
        err.message?.includes('User denied transaction signature')
      ) {
        errorMessage = 'Withdrawal cancelled by user';
      } else if (
        err.message?.includes('transaction execution reverted') ||
        err.code === 'CALL_EXCEPTION'
      ) {
        errorMessage = 'Withdrawal failed. Please try again.';
      }
      setError(errorMessage);
      debugLog('Withdrawal error', { error: err, message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  }, [winningsManagerContract, signer, debugLog, formatEth]);

  // Memoize the lottery details to avoid re-rendering LotteryInfo if the data hasn't changed
  const memoizedLotteryInfo = useMemo(
    () => (
      <LotteryInfo
        timeRemaining={timeRemaining}
        lotteryDetails={lotteryData}
      />
    ),
    [timeRemaining, lotteryData]
  );

  return (
    <LotteryDashboardWrapper>
      <DashboardBackground />

      {/* Scroll Down Indicator */}
      {isConnected && isCorrectNetwork && lotteryData && (
        <ScrollIndicator $visible={showScrollIndicator} onClick={handleScrollDown}>
          <ScrollArrow>
            <ChevronDown size={24} />
          </ScrollArrow>
          <ScrollText>Scroll</ScrollText>
        </ScrollIndicator>
      )}
      
      {/* Network Switch Banner - Highest Priority */}
      {isConnected && !isCorrectNetwork && (
        <NetworkSwitchBanner onSwitchNetwork={switchNetwork} />
      )}

      {/* Toast Messages */}
      {error && (
        <Toast
          message={error}
          type="error"
          onClose={() => setError(null)}
        />
      )}
      {success && (
        <Toast
          message={success}
          type="success"
          onClose={() => setSuccess(null)}
        />
      )}

      <DashboardContent $hasNetworkBanner={isConnected && !isCorrectNetwork}>
        {!isConnected ? (
          <LotteryWelcomeInfo onConnectWallet={handleConnectWallet} />
        ) : isConnected && isCorrectNetwork ? (
          <>
            {(isLoading && isFirstLoad) || !lotteryData ? (
              <LoadingSpinner />
            ) : (
              <>
                {/* Hero Jackpot Section */}
                <HeroJackpot
                  totalPool={lotteryData.formattedTotalPool || '0 ETH'}
                  timeRemaining={timeRemaining}
                  ticketsSold={ticketsSold}
                  lotteryId={lotteryData.lotteryId}
                />

                {/* Main Game Card */}
                <MainCard>
                  <LotteryNumberSelector
                    lotteryDetails={{ maxNum: lotteryData.maxNum }}
                    maxNumbers={lotteryData.numbers}
                    onNumbersSelected={setSelectedNumbers}
                    preselectedNumbers={selectedNumbers}
                    isLoading={isLoading}
                    lotteryState={lotteryData.state}
                    timeRemaining={timeRemaining}
                  />

                  <LotteryActionPanel
                    lotteryData={lotteryData}
                    selectedNumbers={selectedNumbers}
                    pendingWithdrawal={pendingWithdrawal}
                    isLoading={isLoading}
                    purchaseTicket={handlePurchaseTicket}
                    withdrawWinnings={handleWithdrawWinnings}
                    setSelectedNumbers={setSelectedNumbers}
                    timeRemaining={timeRemaining}
                  />

                  {memoizedLotteryInfo}
                </MainCard>

                {/* Trust Badges */}
                <TrustBadges />
              </>
            )}
          </>
        ) : (
          <div />
        )}
      </DashboardContent>
    </LotteryDashboardWrapper>
  );
};

export default React.memo(LotteryDashboard);
